// Blend between the unconstrained and the constrained face rig (?rigconstrain).
//
// The splat face path applies two CONSTRAINTS on top of the ARKit-composed rig:
// the trained-domain clamp (rigBoard.trainedLimits, inside arkitToIsaacRig) and
// the trained subspace projection (mouthSubspace.projectInto). Both are on by
// default and both can only be turned all the way off today (?rigclamp=none,
// ?rigpca=off). On a bundle whose capture is narrow that is a choice between a
// face that cannot speak and a face that blows out — the useful setting is
// between them.
//
// So the caller runs the ARKit frame through the pipeline TWICE: once with
// neither constraint (the loose twin) and once as shipped (the tight rig), then
// blends the two RESULTS here. Blending the outputs rather than loosening either
// stage keeps both arms measured — every intermediate value is a point on the
// segment between two rigs the code already produces, so no weight invents a
// constraint neither arm applies.
//
// The drives that sit between those two stages (the live mouth mix, the blink
// macro) are applied to BOTH twins by the caller, so the only thing this weight
// moves is how much of the clamp + projection reaches the decoders.
//
// The weight lives in the MODULE SINGLETON below rather than in a ref read once
// at load, because the Rig Preview panel edits it live: `driveSplatFace` reads it
// every frame, so it must never move into React state (the graphState rule), and
// a change takes effect on the next frame with no reload — the same reason
// rigControlState is a singleton.

import { DEFAULT_FACE_RIG_WEIGHT } from './faceRigDefaults.js'

/**
 * `rig = loose + (rig - loose) * w`, in place, on the shared prefix of the two.
 *
 * @param {Float32Array} rig    the fully constrained rig; OVERWRITTEN with the blend
 * @param {ArrayLike<number>} loose the same frame with neither constraint applied
 * @param {number} w            1 = the constrained rig unchanged, 0 = the loose one
 * @returns {Float32Array} `rig`, for chaining
 */
export function blendRigInto(rig, loose, w) {
  if (!rig || !loose || !(w < 1)) return rig // w >= 1, NaN, or nothing to blend: leave it
  const n = Math.min(rig.length, loose.length)
  if (w <= 0) {
    // Exactly the loose twin — not `+ 0 * d`, so a NaN or Infinity in the
    // constrained arm can't leak into a run that asked for none of it.
    for (let i = 0; i < n; i++) rig[i] = loose[i]
    return rig
  }
  for (let i = 0; i < n; i++) rig[i] = loose[i] + (rig[i] - loose[i]) * w
  return rig
}


/**
 * The live weight. `blend` is what driveSplatFace reads every frame.
 *
 *   source     'url' when ?rigconstrain typed it, 'panel' once the user edited it,
 *              'default' otherwise — so the panel can say who is deciding and
 *              offer the way back.
 *   initial    the value the URL/default resolved to, i.e. what "reset" restores.
 *   available  a splat pipeline is up and this weight reaches a face. False for a
 *              GLB character, where the whole ARKit->rig path this constrains does
 *              not run — a slider that silently does nothing is worse than none.
 */
export const rigConstrain = {
  blend: DEFAULT_FACE_RIG_WEIGHT, source: 'default', initial: DEFAULT_FACE_RIG_WEIGHT, available: false,
  /**
   * What the constraint is doing RIGHT NOW: the gap between the two twins on the
   * last composed frame, `{ max, moved, l2 }` (worst single control, how many moved
   * by more than 0.01, and the L2 of the whole vector).
   *
   * It exists because "the slider does nothing" and "the slider is doing something
   * you cannot see on this pose" look identical on screen, and they have opposite
   * fixes. Live speech is the case that needs it: the mouth block is already pulled
   * 65% onto the trained manifold by the live mouth mix before either constraint
   * runs, so the part of the face you are watching while it talks moves LESS than a
   * static extreme pose would suggest.
   *
   * null until the twin exists — i.e. at blend 1 nothing is measured, because
   * nothing is composed twice.
   */
  delta: null,
}

/**
 * Measure the gap between the constrained rig and its unconstrained twin.
 *
 * Called before the blend (the blend destroys `rig`), on the same pass, so it costs
 * one extra scan of ~168 floats and allocates nothing per frame — the object is
 * reused.
 */
const deltaOut = { max: 0, moved: 0, l2: 0 }
export function measureConstraintDelta(rig, loose) {
  const n = Math.min(rig.length, loose.length)
  let max = 0, moved = 0, sum = 0
  for (let i = 0; i < n; i++) {
    const d = Math.abs(rig[i] - loose[i])
    if (d > max) max = d
    if (d > 0.01) moved++
    sum += d * d
  }
  deltaOut.max = max; deltaOut.moved = moved; deltaOut.l2 = Math.sqrt(sum)
  rigConstrain.delta = deltaOut
  return deltaOut
}

const subscribers = new Set()

function notify() {
  // A throwing subscriber is a broken listener, not a reason to drop the value
  // the render loop has already taken.
  for (const fn of subscribers) { try { fn(rigConstrain) } catch (e) { console.error('[ogs] rigConstrain subscriber threw:', e) } }
}

/** Subscribe to weight/availability changes (UI cadence). Returns an unsubscribe. */
export function subscribeRigConstrain(fn) {
  subscribers.add(fn)
  return () => subscribers.delete(fn)
}

const clamp01 = (v) => (Number.isFinite(v) ? (v < 0 ? 0 : v > 1 ? 1 : v) : null)

/**
 * Arm the weight from the resolved ?rigconstrain flag, at pipeline start.
 *
 * This is the URL's ONE chance to speak: a later panel edit outranks it, and a
 * character switch re-arms from the same flag rather than inheriting whatever
 * the previous character's panel was left at.
 */
export function armRigConstrain(value) {
  // `value` is null when ?rigconstrain was not typed, and the default applies.
  const url = clamp01(value)
  const v = url ?? DEFAULT_FACE_RIG_WEIGHT
  rigConstrain.blend = v
  rigConstrain.initial = v
  rigConstrain.source = url != null ? 'url' : 'default'
  rigConstrain.available = true
  rigConstrain.delta = null
  notify()
  return v
}

/** Panel/console edit. Out-of-range or non-finite is REFUSED, not clamped silently. */
export function setRigConstrain(value, source = 'panel') {
  const v = clamp01(value)
  if (v == null) { console.warn(`[ogs] rigConstrain: ${value} is not a number in [0, 1] — ignored`); return rigConstrain.blend }
  rigConstrain.blend = v
  rigConstrain.source = source
  notify()
  return v
}

/** Teardown: the next character arms its own weight (armRigConstrain). */
export function resetRigConstrain() {
  rigConstrain.blend = DEFAULT_FACE_RIG_WEIGHT
  rigConstrain.initial = DEFAULT_FACE_RIG_WEIGHT
  rigConstrain.source = 'default'
  rigConstrain.available = false
  rigConstrain.delta = null
  notify()
}
